import { useMemo } from "react";
import type { ModuleId, ModuleRecord } from "../types";
import { LITE_MODULE, type LiteGroup } from "../utils/route";
import { ModuleViewer } from "./ModuleViewer";

/** Familia da calculadora. Serve para AGRUPAR o indice — nao para sinalizar
 *  risco. As cores clinicas (ambar = entra no paciente, azul-violeta = sai do
 *  frasco) ficam reservadas ao conteudo dentro da calculadora; usa-las aqui
 *  como taxonomia diluiria o significado delas. */
type CalcKind = "bolus" | "infusao" | "preparo" | "ajuste" | "fluxo" | "resumo";

const KIND_LABEL: Record<CalcKind, string> = {
  bolus: "Bólus · dose única",
  infusao: "Infusão contínua",
  preparo: "Preparo de solução",
  ajuste: "Ajuste e escore",
  fluxo: "Fluxograma",
  resumo: "Fechamento"
};

/** Ordem de exibicao dos grupos: o que se faz primeiro na beira do leito. */
const KIND_ORDER: CalcKind[] = ["bolus", "infusao", "preparo", "ajuste", "fluxo", "resumo"];

interface CalcEntry {
  /** data-chip (mod 14) ou data-modal (mod 15) — o LITE apenas abre; quem
   *  calcula continua sendo o modulo. */
  id: string;
  label: string;
  sub: string;
  kind: CalcKind;
}

const CALCS: Record<LiteGroup, CalcEntry[]> = {
  adulto: [
    { id: "iot", label: "Intubação", sub: "indutor · BNM", kind: "bolus" },
    { id: "dva", label: "Bólus / dose intermitente", sub: "mg/kg", kind: "bolus" },
    { id: "continua", label: "Infusão contínua", sub: "mcg/kg/min · mL/h", kind: "infusao" },
    { id: "trombolitico", label: "Trombolítico", sub: "TEP · AVCi", kind: "infusao" },
    { id: "eletrolitos", label: "Reposição de eletrólitos", sub: "K · Mg · Ca · P", kind: "preparo" },
    { id: "renal", label: "Função renal / ATB", sub: "ClCr · ajuste de dose", kind: "ajuste" },
    { id: "escores", label: "Escores", sub: "Glasgow · NIHSS · HEART · TEP", kind: "ajuste" },
    { id: "resumo", label: "Cenário-resumo", sub: "prescrição do plantão", kind: "resumo" }
  ],
  ped: [
    { id: "m-pcr", label: "Parada (PCR)", sub: "adrenalina · desfibrilação", kind: "bolus" },
    { id: "m-iot", label: "Intubação", sub: "SRI · tubo", kind: "bolus" },
    { id: "m-cv", label: "Convulsão", sub: "EME · fases", kind: "bolus" },
    { id: "m-neo", label: "Reanimação neonatal", sub: "sala de parto", kind: "bolus" },
    { id: "m-rx", label: "Medicações", sub: "dose · mL · gotas", kind: "bolus" },
    { id: "m-dva", label: "Drogas vasoativas", sub: "mcg/kg/min · mL/h", kind: "infusao" },
    { id: "m-cad", label: "CAD", sub: "fluido · insulina", kind: "infusao" },
    { id: "m-sep", label: "Sepse", sub: "volume · antibiótico", kind: "infusao" },
    { id: "m-hipo", label: "Hipoglicemia", sub: "VIG · preparo da glicose", kind: "preparo" },
    { id: "m-asma", label: "Asma", sub: "≤5a · ≥6a", kind: "ajuste" },
    { id: "f-bls", label: "BLS", sub: "suporte básico", kind: "fluxo" },
    { id: "f-pcr", label: "Parada (PCR)", sub: "algoritmo PALS", kind: "fluxo" },
    { id: "f-brad", label: "Bradicardia", sub: "FC < 60", kind: "fluxo" },
    { id: "f-taq", label: "Taquicardia", sub: "TSV · TV", kind: "fluxo" },
    { id: "m-sum", label: "Cenário-resumo", sub: "prescrição do plantão", kind: "resumo" }
  ]
};

const GROUP_META: Record<LiteGroup, { title: string; sub: string; count: string }> = {
  adulto: { title: "Calculadora adulto", sub: "peso · altura · cenário", count: `${CALCS.adulto.length} calculadoras` },
  ped: { title: "Calculadora pediátrica", sub: "peso · idade · PALS", count: `${CALCS.ped.length} calculadoras e fluxos` }
};

function BoltIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="lite__bolt">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}

function AdultIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="5.5" r="2.8" />
      <path d="M6 21v-5.5a6 6 0 0 1 12 0V21" />
    </svg>
  );
}

function ChildIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="6.5" r="3.4" />
      <path d="M8 21v-4a4 4 0 0 1 8 0v4" />
      <path d="M9 13.5h6" />
    </svg>
  );
}

interface LiteModeProps {
  modules: ModuleRecord[];
  group?: LiteGroup;
  calcId?: string;
  onSelectGroup: (group: LiteGroup) => void;
  onSelectCalc: (group: LiteGroup, calcId: string) => void;
  onBack: () => void;
  onExitLite: () => void;
  onNavigateModule: (moduleId: ModuleId) => void;
}

export function LiteMode({
  modules,
  group,
  calcId,
  onSelectGroup,
  onSelectCalc,
  onBack,
  onExitLite,
  onNavigateModule
}: LiteModeProps) {
  const moduleRecord = useMemo(
    () => (group ? modules.find((m) => m.id === LITE_MODULE[group]) : undefined),
    [modules, group]
  );

  const entries = group ? CALCS[group] : [];
  const activeCalc = calcId ? entries.find((entry) => entry.id === calcId) : undefined;

  // Nivel 3 — a calculadora aberta. O modulo inteiro e renderizado (unica fonte
  // da logica de dose) e o chip correspondente e aberto por postMessage.
  if (group && calcId && moduleRecord) {
    return (
      <section className="lite lite--calc" aria-label={`${GROUP_META[group].title}: ${activeCalc?.label ?? calcId}`}>
        <header className="lite__bar">
          <button type="button" className="lite__back" onClick={onBack}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 19 8 12l7-7" /></svg>
            <span>{GROUP_META[group].title}</span>
          </button>
          <span className="lite__bar-title">{activeCalc?.label ?? ""}</span>
        </header>
        <ModuleViewer
          module={moduleRecord}
          modules={modules}
          onNavigateModule={onNavigateModule}
          openCalcId={calcId}
          lite
        />
      </section>
    );
  }

  // Nivel 2 — indice de calculadoras do bloco, agrupado por familia.
  if (group) {
    return (
      <section className="lite" aria-label={GROUP_META[group].title}>
        <header className="lite__bar">
          <button type="button" className="lite__back" onClick={onBack}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 19 8 12l7-7" /></svg>
            <span>LITE</span>
          </button>
          <span className="lite__bar-title">{GROUP_META[group].title}</span>
        </header>

        <div className="lite__index">
          {KIND_ORDER.map((kind) => {
            const ofKind = entries.filter((entry) => entry.kind === kind);
            if (ofKind.length === 0) return null;
            return (
              <div key={kind} className="lite__family">
                <h3 className="lite__family-title">{KIND_LABEL[kind]}</h3>
                <div className="lite__grid">
                  {ofKind.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className="lite__calc"
                      onClick={() => onSelectCalc(group, entry.id)}
                    >
                      <span className="lite__calc-label">{entry.label}</span>
                      <span className="lite__calc-sub">{entry.sub}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <p className="lite__note">
          Cada calculadora abre o módulo completo por baixo — mesma conta, mesmas fontes.
          <button type="button" className="lite__link" onClick={onExitLite}>Sair do modo LITE</button>
        </p>
      </section>
    );
  }

  // Nivel 1 — escolha entre os dois blocos.
  return (
    <section className="lite lite--root" aria-label="Modo LITE">
      <header className="lite__hero">
        <span className="lite__eyebrow"><BoltIcon />Modo LITE</span>
        <h2 className="lite__title">Calculadoras, direto ao ponto</h2>
        <p className="lite__lead">Duas toques até a dose. Sem texto de apoio no caminho.</p>
      </header>

      <div className="lite__groups">
        <button type="button" className="lite__group" onClick={() => onSelectGroup("adulto")}>
          <span className="lite__group-ico"><AdultIcon /></span>
          <span className="lite__group-title">{GROUP_META.adulto.title}</span>
          <span className="lite__group-sub">{GROUP_META.adulto.sub}</span>
          <span className="lite__group-count">{GROUP_META.adulto.count}</span>
        </button>
        <button type="button" className="lite__group" onClick={() => onSelectGroup("ped")}>
          <span className="lite__group-ico"><ChildIcon /></span>
          <span className="lite__group-title">{GROUP_META.ped.title}</span>
          <span className="lite__group-sub">{GROUP_META.ped.sub}</span>
          <span className="lite__group-count">{GROUP_META.ped.count}</span>
        </button>
      </div>

      <p className="lite__note">
        <button type="button" className="lite__link" onClick={onExitLite}>Voltar ao manual completo</button>
      </p>
    </section>
  );
}
